<template>
  <div class="date-request-container">
    <Alert
      v-if="
        !isPrivateEventRequest &&
        dateRequestSidebarRequestPreferredLocation === 'user_location'
      "
      class="my-8 kft-alert--warning"
      type="warning"
      :content="
        t('product.dateRequest.eventSuggestions.cantBeHeldAtCustomLocation')
      "
    />
    <!-- Form Content - shown when not in available dates layer -->
    <form
      v-if="!showAvailableDatesLayer"
      class="w-100 pb-4"
      data-test-id="contact-form"
      @submit.prevent.stop="requestEventDates()"
    >
      <DateRequestUserInfo
        :key="`user-info-${formUpdateTrigger}`"
        v-model="form"
        :event-data="(eventData as any) || {}"
        :is-event-request-opened-from-event-page="isFromEventPage"
        :is-private-event-request="isPrivateEventRequest"
        :is-date-request-applicable="isDateRequestApplicable"
        :private-event-min-tickets="PRIVATE_EVENT_MIN_TICKETS"
        :errors="errors"
        :invalid-keys="invalidKeys as any"
        @clear-errors="(field) => clearErrors(field)"
      />
      <div v-if="isDateRequestApplicable" class="mt-4">
        <Tooltip
          :key="
            t(
              'product.subscribeToEventUpdatesModal.firstStep.form.dateType.toolTipText'
            )
          "
          :tooltip-text="
            t(
              'product.subscribeToEventUpdatesModal.firstStep.form.dateType.toolTipText'
            )
          "
          :disabled="!eventGetters.getIsTeamEvent(eventData)"
          length="large"
          type="span"
          class="self-center mb-2 block"
        >
          <label for="date-type-select" class="block mb-2 required">
            {{
              t(
                'product.subscribeToEventUpdatesModal.firstStep.form.dateType.label'
              )
            }}
          </label>
        </Tooltip>
        <select
          id="date-type-select"
          :key="`date-type-${formUpdateTrigger}`"
          v-model="dateTypeModel"
          :class="[
            'date-type-select w-full rounded border px-3 py-2 text-sm',
            invalidKeys.includes('dateType') ? 'border-red' : 'border-gray-300',
          ]"
          data-test-id="date-type-select"
          @change="clearErrors('dateType')"
        >
          <option value="" disabled style="display: none">
            {{
              t(
                'product.subscribeToEventUpdatesModal.firstStep.form.dateType.placeholder'
              )
            }}
          </option>
          <option value="PUBLIC">
            {{
              t(
                'product.subscribeToEventUpdatesModal.firstStep.form.dateType.public'
              )
            }}
          </option>
          <option value="PRIVATE">
            {{
              t(
                'product.subscribeToEventUpdatesModal.firstStep.form.dateType.private'
              )
            }}
          </option>
        </select>
        <span
          v-if="invalidKeys.includes('dateType')"
          data-test-id="date-type-validation-error"
          class="text-red text-sm block mt-1"
        >
          {{ errors?.dateType || t('general.form.validation.required') }}
        </span>
      </div>
      <PrivateEventDate
        v-if="isPrivateEventRequest"
        :key="`private-event-${formUpdateTrigger}`"
        v-model="form"
        :event-data="(eventData as any) || {}"
        :is-from-event-page="isFromEventPage"
        :errors="errors"
        :invalid-keys="invalidKeys as any"
      />
      <DateRequestDates
        v-else-if="isDateRequestApplicable"
        :key="`date-request-${formUpdateTrigger}`"
        v-model="form"
        :event-data="(eventData as any) || {}"
        :available-dates="availableDates"
        :errors="errors"
        :invalid-keys="invalidKeys as any"
        @clear-errors="(field) => clearErrors(field)"
      />
      <label v-if="isPrivateEventRequest" class="cursor-pointer block text-sm">
        <input
          id="checkbox"
          v-model="canUserBeContactedChecked"
          type="checkbox"
          class="mt-4 mr-2"
        />
        {{
          t(
            'product.subscribeToEventUpdatesModal.firstStep.form.confirmContactCheckbox.label'
          )
        }}
      </label>
      <span
        v-if="!isContactConfirmationChecked && isPrivateEventRequest"
        data-test-id="date-request-form-invalid"
        class="text-red text-sm block"
      >
        {{ t('general.form.validation.required') }}
      </span>
      <!-- Continue Button - shown when matching dates are available -->
      <Button
        v-if="
          showAvailableDates &&
          !showAvailableDatesLayer &&
          !isPrivateEventRequest
        "
        data-test-id="buy-box-date-request-continue-button"
        class="mt-4 w-full"
        :disabled="loading || calendarLoading"
        @click.prevent="showAvailableDatesLayer = true"
      >
        <span>{{ t('general.continue') }}</span>
      </Button>

      <!-- Add to Cart Button - shown when dates are selected -->
      <Button
        v-if="showAvailableDatesLayer && selectedAvailableDates?.length > 0"
        data-test-id="buy-box-date-request-add-to-cart-button"
        class="mt-4 w-full sm:w-auto"
        :disabled="loading || calendarLoading"
        @click.prevent="availableDatesSidebarRef?.addSelectedDatesToCart()"
      >
        <span>{{ t('product.dateRequest.addToCart') }}</span>
      </Button>

      <!-- Default Submit Button - shown for other cases -->
      <Button
        v-if="!showAvailableDates || isPrivateEventRequest"
        data-test-id="buy-box-date-request-submit-button"
        class="mt-4 w-full sm:w-auto"
        :disabled="loading || calendarLoading"
        @click.prevent="handleSubmitButtonClick"
      >
        <span>{{ buttonText }}</span>
      </Button>
    </form>

    <DateRequestMatchingDatesTab
      ref="availableDatesSidebarRef"
      :event-data="eventData"
      :form="form"
      :show-matching-dates-layer="showAvailableDatesLayer"
      @add-to-cart="handleAddToCart"
      @close="handleRequestOtherDates"
      @availability-changed="
        (hasAvailableDates: boolean) => (showAvailableDates = hasAvailableDates)
      "
    />
  </div>
</template>

<script lang="ts" setup>
import { useFormValidation } from '~/composables/useFormValidation'
import { useDateRequestFormStorage } from '~/composables/useDateRequestFormStorage'
import { useDateRequest } from '~/composables/useDateRequest'
import 'vue3-toastify/dist/index.css'
import type { EventDateType } from '~/composables/types/event'

const PRIVATE_EVENT_MIN_TICKETS = 11
const canUserBeContactedChecked = ref(false)
const isContactConfirmationChecked = ref(true)
const router = useRouter()
const $localePath = useLocalePath()
const localePath = $localePath
const eventData = useEventData()
const loading = ref(false)
const availableDatesSidebarRef = ref()

// Initialize local storage for form data
const { savedFormData, loaded } = useDateRequestFormStorage()

// Add a reactive trigger to force child component updates
const formUpdateTrigger = ref(0)

const form = ref<{
  email: string
  phone: string
  name: string
  surname: string
  numberOfTickets: number | null
  companyName: string
  city: string
  dateType: EventDateType | null
  dates: string[]
  additionalInfo?: string
}>({
  email: '',
  phone: '',
  name: '',
  surname: '',
  numberOfTickets: null,
  companyName: '',
  city: '',
  dateType: null,
  dates: [],
  additionalInfo: '',
})

const dateTypeModel = computed({
  get: () => form.value.dateType ?? '',
  set: (v: EventDateType | null | '') => {
    form.value.dateType = v === '' ? null : v
  },
})

const isFromEventPage = computed(
  () => eventRequestOpenedPage?.value?.split('___')[0] === 'e-eventPermalink'
)

const originalRules = {
  email: 'required|email',
  name: 'required|min:2',
  surname: 'required|min:2',
  phone: 'required|phoneNumber',
  numberOfTickets: `required|numeric|min:${eventGetters.getMinDateRequestTickets(
    eventData.value
  )}|max:${eventGetters.getMaxDateRequestTickets(eventData.value)}`,
}

const rules = ref(originalRules)
const { requestEventDates: requestEventDatesComposable } = useDateRequest()

const { t } = useI18n()
const emit = defineEmits([
  'on-complete-request',
  'onCloseDateRequest',
  'on-complete-private-request',
])

const {
  toggleDateRequestSidebar,
  eventRequestOpenedPage,
  dateRequestSidebarRequestPreferredLocation,
} = useUiState()
const { errors, clearErrors, validate, invalidKeys } = useFormValidation(
  form,
  rules
)

const isPrivateEventRequest = computed(() => {
  if (!form.value?.numberOfTickets || form.value?.numberOfTickets === 0)
    return true
  return form.value?.numberOfTickets >= PRIVATE_EVENT_MIN_TICKETS
})

const isDateRequestEnabledForTheEvent = computed(() =>
  eventGetters.isDateRequestActive(eventData.value)
)

const isDateRequestApplicable = computed(() => {
  return (
    isFromEventPage.value &&
    !isPrivateEventRequest.value &&
    isDateRequestEnabledForTheEvent.value
  )
})

const handleSubmitButtonClick = () => {
  if (isPrivateEventRequest.value) {
    requestPrivateEvent()
    return
  }
  if (isDateRequestApplicable.value) {
    requestEventDates()
    return
  }

  handleClickSeeMore()
}

const handleClickSeeMore = () => {
  toggleDateRequestSidebar(undefined)
  emit('onCloseDateRequest')
  router.push({
    path: localePath('search'),
  })
}

const showAvailableDates = ref(false)
const selectedAvailableDates = computed(
  () => availableDatesSidebarRef.value?.selectedAvailableDates
)
const calendarLoading = computed(
  () => availableDatesSidebarRef.value?.calendarLoading
)
const availableDates = computed(
  () => availableDatesSidebarRef.value?.availableDates || []
)
const showAvailableDatesLayer = ref(false)

const buttonText = computed(() => {
  if (isDateRequestApplicable.value || isPrivateEventRequest.value) {
    return t(
      'product.subscribeToEventUpdatesModal.firstStep.requestDatesButtonLabel'
    )
  }
  return t('product.dateRequest.eventSuggestions.seeMoreButton')
})

const handleAddToCart = () => {
  emit('onCloseDateRequest')
}

const handleRequestOtherDates = () => {
  showAvailableDatesLayer.value = false
}

const requestEventDates = async () => {
  rules.value = {
    ...originalRules,
    phone: 'phoneNumber',
    dates: 'required|array',
    dateType: 'required|in:PUBLIC,PRIVATE',
  } as any
  await validate()
  if (form.value.dates?.length === 0) {
    loading.value = false
    return
  }

  if (errors.value !== null && Object.keys(errors.value)?.length > 0) {
    loading.value = false
    return
  }

  if (form.value.dateType !== 'PUBLIC' && form.value.dateType !== 'PRIVATE') {
    loading.value = false
    return
  }

  try {
    await requestEventDatesComposable({
      eventDescriptionId: eventGetters.getId(eventData.value),
      form,
      isPrivateEvent: false,
      onSuccess: (dateRequestId?: string) => {
        emit('on-complete-request', dateRequestId)
      },
      onError: (_error: any) => {
        // Error handling is already done in the composable
      },
    })
  } finally {
    loading.value = false
  }
}
const requestPrivateEvent = async () => {
  loading.value = true
  rules.value = {
    ...originalRules,
    phone: originalRules.phone,
    numberOfTickets: 'required',
  }
  delete (rules.value as Record<string, unknown>)?.dates
  isContactConfirmationChecked.value = canUserBeContactedChecked.value
  await validate()

  if (errors.value !== null && Object.keys(errors.value)?.length > 0) {
    loading.value = false
    return
  }

  if (!canUserBeContactedChecked.value) {
    loading.value = false
    return
  }

  try {
    await requestEventDatesComposable({
      eventDescriptionId: eventGetters.getId(eventData.value),
      form,
      isPrivateEvent: true,
      onSuccess: () => {
        emit('on-complete-private-request')
      },
      // onError: (error) => {
      // Error handling is already done in the composable
      // }
    })
  } finally {
    loading.value = false
  }
}

watch(
  () => isPrivateEventRequest.value,
  () => {
    clearErrors()
    isContactConfirmationChecked.value = true
  }
)
watch(
  () => canUserBeContactedChecked.value,
  () => {
    isContactConfirmationChecked.value = canUserBeContactedChecked.value
  }
)

// Watch for when localStorage data is loaded and update form
watch(
  () => loaded.value,
  async (isLoaded) => {
    if (isLoaded && savedFormData.value) {
      form.value = {
        ...savedFormData.value,
        dateType:
          savedFormData.value.dateType === 'PRIVATE' ||
          savedFormData.value.dateType === 'PUBLIC'
            ? savedFormData.value.dateType
            : null,
        dates: [...savedFormData.value.dates], // Convert readonly array to mutable array
      }

      // Force child components to update
      await nextTick()
      formUpdateTrigger.value++
    }
  }
)

// Watch for changes in savedFormData and update form accordingly
watch(
  () => savedFormData.value,
  (newData) => {
    if (newData && loaded.value) {
      form.value = {
        ...newData,
        dateType:
          newData.dateType === 'PRIVATE' || newData.dateType === 'PUBLIC'
            ? newData.dateType
            : null,
        dates: [...newData.dates], // Convert readonly array to mutable array
      }
    }
  },
  { deep: true }
)

defineExpose({
  showMatchingDatesLayer: showAvailableDatesLayer,
  closeMatchingDatesLayer: () => {
    showAvailableDatesLayer.value = false
  },
})
</script>
<style scoped lang="postcss">
.date-type-select {
  @apply accent-teal-600;
  @apply rounded-lg h-10;
  &:disabled {
    @apply cursor-not-allowed opacity-60;
  }
}

.required:after {
  content: '*';
  position: relative;
  font-size: inherit;
  @apply text-red;
  padding-left: 0.25rem;
  font-weight: 700;
}
</style>
